/* ── KOR-E shared JS-gated image loading ─────────────────────────────────────
   One utility for every section that ships images without src/srcset so the
   browser's native lazy-load lookahead (2–3 viewports) cannot prefetch them
   into the pre-LCP bandwidth window.

   Markup contract:
     <img data-kore-gate-src="..." data-kore-gate-srcset="..." sizes="..." ...>

   Behavior (identical to the five per-section copies this replaces):
   - one IntersectionObserver, rootMargin 600px vertical / 300px horizontal;
   - on approach, srcset then src are applied and the img is unobserved;
   - browsers without IntersectionObserver load everything immediately;
   - a CSS-hidden element (display:none device-duplicate sections) never
     intersects, so it never downloads;
   - re-scans on shopify:section:load so theme-editor reloads keep working;
   - window.koreGateScan(container) is the public re-scan hook for code that
     appends gated markup via AJAX (e.g. the shop page's Load More). */
(function () {
  'use strict';

  function loadImg(img) {
    if (img._koreGateLoaded) return;
    img._koreGateLoaded = true;
    /* kore-gate-loaded fires on decode-complete so sections can fade the image in
       instead of having it pop (opt-in via CSS; no section is required to use it) */
    img.addEventListener('load', function () { img.classList.add('kore-gate-loaded'); }, { once: true });
    /* Flaky-cellular backstop: a dropped transfer fires 'error' and the browser
       never retries an <img> on its own, which left the box empty forever
       (user-reported: media+text image "sometimes fails to load" on mobile
       data). Re-apply the source up to 2 times with backoff. */
    img.addEventListener('error', function () {
      var n = (img._koreGateRetries = (img._koreGateRetries || 0) + 1);
      if (n > 2) return;
      setTimeout(function () {
        if (img.complete && img.naturalWidth > 0) return;
        img.removeAttribute('srcset');
        img.removeAttribute('src');
        if (img.dataset.koreGateSrcset) img.setAttribute('srcset', img.dataset.koreGateSrcset);
        img.setAttribute('src', img.dataset.koreGateSrc);
      }, 1500 * n);
    });
    if (img.dataset.koreGateSrcset) img.setAttribute('srcset', img.dataset.koreGateSrcset);
    img.setAttribute('src', img.dataset.koreGateSrc);
    if (img.complete && img.naturalWidth > 0) img.classList.add('kore-gate-loaded');
  }

  /* ── Hero hold ─────────────────────────────────────────────────────────────
     The 600px margin releases ~6 near-fold images (~470KB) at page load, and on
     mobile they share the link with the LCP hero — measured +550ms on homepage
     LCP (A/B, 3+3 runs, 1.6Mbps). While a high-priority image is still loading,
     defer only OFF-viewport releases; images inside the actual viewport load
     immediately (on collection pages the LCP element is itself a gated image —
     holding those would regress LCP there). Cap guards against a broken hero. */
  var holdActive = false;
  var heldImgs = [];
  function releaseHold() {
    holdActive = false;
    for (var i = 0; i < heldImgs.length; i++) loadImg(heldImgs[i]);
    heldImgs = [];
  }
  (function () {
    var hero = document.querySelector('img[fetchpriority="high"]');
    if (!hero || hero.complete) return;
    holdActive = true;
    hero.addEventListener('load', releaseHold, { once: true });
    hero.addEventListener('error', releaseHold, { once: true });
    setTimeout(releaseHold, 2500);
  })();

  var obs = null;
  if ('IntersectionObserver' in window) {
    obs = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (!e.isIntersecting) return;
        obs.unobserve(e.target);
        if (holdActive) {
          var r = e.target.getBoundingClientRect();
          if (r.bottom < 0 || r.top > window.innerHeight || r.right < 0 || r.left > window.innerWidth) {
            heldImgs.push(e.target);
            return;
          }
        }
        loadImg(e.target);
      });
    }, { rootMargin: '600px 300px 600px 300px' });
  }

  /* ── Hover-only images ────────────────────────────────────────────────────
     Product cards carry a second image that is revealed ONLY by
     `.card-wrapper:hover .media--hover-effect > img + img` inside
     `@media screen and (min-width: 990px)`. Below that width it is unreachable —
     opacity 0 with no hover to change it — so attaching a src just burns the
     visitor's bandwidth. Measured on /collections/all at 390px: 21 such images,
     509KB, 47% of all image bytes on the page.
     These are skipped WITHOUT setting _koreGateObserved, so a later rescan can
     still pick them up when the viewport crosses 990px. */
  var DESKTOP = window.matchMedia ? window.matchMedia('(min-width: 990px)') : null;
  function hoverOnlyBlocked(img) {
    return img.hasAttribute('data-kore-hover-only') && !(DESKTOP && DESKTOP.matches);
  }

  function scan(root) {
    var imgs = (root && root.querySelectorAll ? root : document).querySelectorAll('img[data-kore-gate-src]');
    for (var i = 0; i < imgs.length; i++) {
      var img = imgs[i];
      if (hoverOnlyBlocked(img)) continue;   /* deliberately NOT marked observed */
      if (!obs) { loadImg(img); continue; }
      if (img._koreGateObserved) continue;
      img._koreGateObserved = true;
      obs.observe(img);
    }
  }

  /* Crossing into desktop width releases the hover images that were held back. */
  if (DESKTOP) {
    var onBreakpoint = function () { if (DESKTOP.matches) scan(document); };
    if (DESKTOP.addEventListener) DESKTOP.addEventListener('change', onBreakpoint);
    else if (DESKTOP.addListener) DESKTOP.addListener(onBreakpoint);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { scan(document); });
  } else {
    scan(document);
  }
  document.addEventListener('shopify:section:load', function (e) { scan(e.target || document); });
  window.koreGateScan = scan;
  /* Force-load a single gated image immediately, bypassing the viewport gate —
     for code that is about to SHOW the image somewhere else (e.g. the UGC modal
     borrowing a card poster whose card is still outside the gate margin). */
  window.koreGateLoadNow = loadImg;
})();
